import React, { useReducer, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../SharedInputs.jsx";
import { S } from "../styles.js";
import { domesticReducer, initialStateDomestic, reducer } from "./state.js";
import { DOMESTIC_SECTION_KEYS, SECTION_KEYS, SECTION_LABELS, SECTION_LABELS_DOMESTIC } from "../../../constants.js"; // Add human-readable labels
import { validateSection, validateAll, saveFormProgress } from './utils.js';
import { updateEnrollment } from "../../../services/EnrolmentService.js";
import {fileStorageService} from "../../../services/FileStorageService.js";

// Sections
import { SectionPersonal } from './sections/SectionPersonal.jsx';
import { SectionCampusCourse } from './sections/SectionCampusCourse.jsx';
import { SectionIntake } from './sections/SectionIntake.jsx';
import { SectionVisa } from './sections/SectionVisa.jsx';
import { SectionLanguageCulture } from './sections/SectionLanguageCulture.jsx';
import { SectionDisability } from './sections/SectionDisability.jsx';
import { SectionSchoolingQualifications } from './sections/SectionSchoolingQualifications.jsx';
import { SectionOSHC } from './sections/SectionOSHC.jsx';
import { SectionEmploymentStudy } from './sections/SectionEmploymentStudy.jsx';
import { SectionAdditionalServices } from './sections/SectionAdditionalServices.jsx';
import { SectionRPLCredit } from './sections/SectionRPLCredit.jsx';
import { SectionUSI } from './sections/SectionUSI.jsx';
import { SectionDeclaration } from './sections/SectionDeclaration.jsx';
import { SectionAction } from './sections/SectionAction.jsx';

export default function ViewEnrolmentForm({ initialData, onCancel, readOnly = false }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [parentInitialData, setParentInitialData] = useState(initialData || {}); 
  const [isDomestic, setDomestic] = useState(initialData?.category === 'domestic' || false);
  const [sectionKeys, setSectionKeys] = useState(initialData?.category === 'domestic' ? DOMESTIC_SECTION_KEYS : SECTION_KEYS);
  const [sectionLabels, setSectionLabels] = useState(initialData?.category === 'domestic' ? SECTION_LABELS_DOMESTIC : SECTION_LABELS);
  const [state, dispatch] = useReducer(isDomestic ? domesticReducer : reducer, (isDomestic ? initialData.formState : initialData?.formState) || {});
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(null);
  // Auto-save only if not read-only
  useEffect(() => {
    if (!readOnly) {
      const timeoutId = setTimeout(() => saveFormProgress(state), 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [state, readOnly]);

  // Update completed steps
  useEffect(() => {
    const newCompleted = [];
    sectionKeys.forEach((key, index) => {
      const valid = readOnly ? true : validateSection(key, state).valid;
      if (valid) newCompleted.push(index);
    });
    setCompletedSteps(newCompleted);
  }, [state, currentStep, readOnly]);

  const renderSection = () => {
    let key = sectionKeys[currentStep];
    if ((isDomestic && currentStep === 12) || currentStep === 14) {
      key = "actions";
    }
    const data = state[key];
    const validation = readOnly ? {} : validateSection(key, state);
    const commonProps = {
      value: data,
      onChange: readOnly ? undefined : payload => dispatch({ type: "SET", section: key, payload }),
      errors: validation.errors,
      readOnly,
      dispatch,
      onCancel,
      initialData: parentInitialData,
      setParentInitialData, // pass setter to SectionAction
      category: isDomestic ? 'domestic' : 'international',
    };

    const sectionsMap = {
      personal: SectionPersonal,
      campusCourse: SectionCampusCourse,
      intake: SectionIntake,
      visa: SectionVisa,
      languageCulture: SectionLanguageCulture,
      disability: SectionDisability,
      schoolingQualifications: SectionSchoolingQualifications,
      oshc: SectionOSHC,
      employmentStudyReason: SectionEmploymentStudy,
      additionalServices: SectionAdditionalServices,
      rplCredit: SectionRPLCredit,
      usi: SectionUSI,
      declaration: SectionDeclaration,
      actions: SectionAction,
    };

    const SectionComponent = sectionsMap[key];
    return SectionComponent ? <SectionComponent {...commonProps} /> : <div>Section not found</div>;
  };

  const handleSubmit = async () => {
    const validation = validateAll(state, sectionKeys);

    if (!validation.valid) {
      setSubmitError(
        "Please complete all required fields in each section before submitting."
      );
      return;
    }

    setSubmitError("");
    setSubmitting(true);

    try {
      const enrollmentId = initialData.id; // always update
      const stateWithUrls = { ...state };

      // --- Helper to handle file deletion & upload ---
      const processFiles = async (currentFiles, existingFiles, storagePath) => {
        const removedFiles = existingFiles.filter(
          f => f && !(currentFiles.includes(f) || currentFiles.some(cf => cf instanceof File && cf.name === f.name))
        );

        // Delete removed files from storage
        for (const f of removedFiles) {
          if (f?.path) {
            try {
              await fileStorageService.deleteFile(f.path);
            } catch (err) {
            }
          }
        }

        // Split new vs already uploaded
        const existing = currentFiles.filter(f => !(f instanceof File));
        const newFiles = currentFiles.filter(f => f instanceof File);

        // Upload new files
        let uploaded = [];
        if (newFiles.length) {
          const filesData = await fileStorageService.uploadFiles(newFiles, storagePath);
          uploaded = filesData.map((f, i) => ({
            url: f.url,
            path: f.path,
            name: newFiles[i].name
          }));
        }

        return [...existing, ...uploaded];
      };

      // --- Visa Files ---
      stateWithUrls.visa = stateWithUrls.visa || {};
      const visaCurrent = state.visa?.currentVisaCopy ? [state.visa.currentVisaCopy] : [];
      const visaRefusal = state.visa?.refusalLetter ? [state.visa.refusalLetter] : [];
      const existingVisaCurrent = initialData?.formState?.visa?.currentVisaCopy ? [initialData.formState.visa.currentVisaCopy] : [];
      const existingVisaRefusal = initialData?.formState?.visa?.refusalLetter ? [initialData.formState.visa.refusalLetter] : [];

      stateWithUrls.visa.currentVisaCopy = (await processFiles(
        visaCurrent,
        existingVisaCurrent,
        visaCurrent[0] ? `enrolment/${enrollmentId}/visa/${visaCurrent[0].name}` : `enrolment/${enrollmentId}/visa/currentVisaCopy`
      ))[0] || null;

      stateWithUrls.visa.refusalLetter = (await processFiles(
        visaRefusal,
        existingVisaRefusal,
        visaRefusal[0] ? `enrolment/${enrollmentId}/visa/${visaRefusal[0].name}` : `enrolment/${enrollmentId}/visa/refusalLetter`
      ))[0] || null;

      // --- USI Exemption ---
      stateWithUrls.usi = stateWithUrls.usi || {};
      const usiFiles = state.usi?.usiExemptionLetter ? [state.usi.usiExemptionLetter] : [];
      const existingUsi = initialData?.formState?.usi?.usiExemptionLetter ? [initialData.formState.usi.usiExemptionLetter] : [];
      stateWithUrls.usi.usiExemptionLetter = (await processFiles(
        usiFiles,
        existingUsi,
        usiFiles[0] ? `enrolment/${enrollmentId}/usi/${usiFiles[0].name}` : `enrolment/${enrollmentId}/usi/usiExemptionLetter`
      ))[0] || null;

      // --- Schooling Transcripts ---
      stateWithUrls.schoolingQualifications = stateWithUrls.schoolingQualifications || {};
      const transcripts = state.schoolingQualifications?.transcripts || [];
      const existingTranscripts = initialData?.formState?.schoolingQualifications?.transcripts || [];
      stateWithUrls.schoolingQualifications.transcripts = await processFiles(
        transcripts,
        existingTranscripts,
        `enrolment/${enrollmentId}/transcripts`
      );

      // --- RPL Credit Evidence ---
      stateWithUrls.rplCredit = stateWithUrls.rplCredit || {};
      const rplFiles = state.rplCredit?.creditEvidence || [];
      const existingRpl = initialData?.formState?.rplCredit?.creditEvidence || [];
      stateWithUrls.rplCredit.creditEvidence = await processFiles(
        rplFiles,
        existingRpl,
        `enrolment/${enrollmentId}/rplCreditEvidence`
      );

      // --- Passport ---
      stateWithUrls.personal = stateWithUrls.personal || {};
      const passportFiles = state.personal?.passportCopy ? [state.personal.passportCopy] : [];
      const existingPassport = initialData?.formState?.personal?.passportCopy ? [initialData.formState.personal.passportCopy] : [];
      stateWithUrls.personal.passportCopy = (await processFiles(
        passportFiles,
        existingPassport,
        passportFiles[0] ? `enrolment/${enrollmentId}/personal/${passportFiles[0].name}` : `enrolment/${enrollmentId}/personal/passportCopy`
      ))[0] || null;

      // --- Language file ---
      stateWithUrls.languageCulture = stateWithUrls.languageCulture || {};
      const languageFiles = state.languageCulture?.currentEnglishCopy ? [state.languageCulture.currentEnglishCopy] : [];
      const existingLanguage = initialData?.formState?.languageCulture?.currentEnglishCopy ? [initialData.formState.languageCulture.currentEnglishCopy] : [];
      stateWithUrls.languageCulture.currentEnglishCopy = (await processFiles(
        languageFiles,
        existingLanguage,
        languageFiles[0] ? `enrolment/${enrollmentId}/languageCulture/${languageFiles[0].name}` : `enrolment/${enrollmentId}/languageCulture/currentEnglishCopy`
      ))[0] || null;

      // --- Additional documents ---
      stateWithUrls.declaration = stateWithUrls.declaration || {};
      const additionalFiles = state.declaration?.additionalDocuments || [];
      const existingAdditional = initialData?.formState?.declaration?.additionalDocuments || [];
      stateWithUrls.declaration.additionalDocuments = await processFiles(
        additionalFiles,
        existingAdditional,
        `enrolment/${enrollmentId}/additionalDocuments`
      );



      // --- Firestore Update ---
      await updateEnrollment(enrollmentId, stateWithUrls, initialData.enrolmentReferenceId, initialData?.category);
      setSubmitting(false);
      setSubmitSuccess(enrollmentId);
      onCancel();
    } catch (error) {
      setSubmitting(false);
      setSubmitError(
        "Submission failed. Please try again or contact admissions support."
      );
    }
  };



return (
  <div style={S.enrolmentPage}>
    <motion.div style={{ ...S.card, display: "flex", flexDirection: "column" }}>
      
      {/* Top Actions Header */}
      <div
        className="actions"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: "1rem",
          borderBottom: "1px solid #ddd",
          paddingBottom: "0.5rem",
        }}
      >
        <div>
          <h1 className="page-title">New Enrolment</h1>
          <p className="page-subtitle">Create and manage Enrolment</p>
        </div>

        <div style={{ display: "flex", gap: "1rem" }}>
          <Button
            type="button"
            className="btn btn-secondary"
            variant="secondary"
            onClick={onCancel}
          >
            Back
          </Button>
          <Button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Save"}
          </Button>

        </div>
      </div>

      {/* Main Content Area (tabs + section content) */}
      <div style={{ display: "flex", flex: 1 }}>
        {/* Left Panel Tabs */}
        <div
          style={{
            width: 220,
            borderRight: "2px solid #ccc",
            display: "flex",
            flexDirection: "column",
            paddingTop: 16,
            backgroundColor: "#f9f9f9",
          }}
        >
          {sectionKeys.map((key, index) => (
            <div
              key={key}
              onClick={() => setCurrentStep(index)}
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                fontWeight: currentStep === index ? "bold" : "normal",
                color: currentStep === index ? "#007bff" : "#333",
                backgroundColor: currentStep === index ? "#e6f0ff" : "transparent",
                borderLeft:
                  currentStep === index ? "4px solid #007bff" : "4px solid transparent",
                position: "relative",
                transition: "all 0.2s ease-in-out",
                marginBottom: 4,
              }}
            >
              {sectionLabels[key] || key}
              {completedSteps.includes(index) && (
                <span
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#28a745",
                    fontSize: 14,
                  }}
                >
                  ✔
                </span>
              )}
            </div>
          ))}
          <div
            key={"actions"}
            onClick={() => setCurrentStep(isDomestic? 12 : 14)}
            style={{
              padding: "12px 16px",
              cursor: "pointer",
              fontWeight: ((isDomestic && currentStep === 12) || currentStep === 14) ? "bold" : "normal",
                color: ((isDomestic && currentStep === 12) || currentStep === 14) ? "#007bff" : "#007bff",
                backgroundColor: ((isDomestic && currentStep === 12) || currentStep === 14) ? "#e6f0ff" : "transparent",
                borderLeft:
                  ((isDomestic && currentStep === 12) || currentStep === 14) ? "4px solid #007bff" : "4px solid transparent",
                borderTop:
                  ((isDomestic && currentStep === 12) || currentStep === 14) ? "4px solid transparent" : "1px solid #007bff",
              position: "relative",
              transition: "all 0.2s ease-in-out",
              marginBottom: 4,
            }}
          >
            {sectionLabels["actions"] || "actions"}
          </div>


        </div>

        {/* Right Panel Section Content */}
        <div style={{ flex: 1, padding: 16 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
            >
              {renderSection()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  </div>
);
}
